import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TID } from "../../lib/testIds";

const points = [
    "Better learning outcomes.",
    "Smarter institutions.",
    "Data-driven decisions.",
    "Modern student experiences.",
];

// One word reveal — its own component so hooks aren't called in a loop
const Word = ({ progress, start, end, children }) => {
    const opacity = useTransform(progress, [start, end], [0.15, 1]);
    return (
        <motion.span style={{ opacity }} className="inline-block mr-[0.25em]">
            {children}
        </motion.span>
    );
};

// Word-by-word reveal on scroll
const ScrollText = ({ text }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.85", "end 0.4"],
    });
    const words = text.split(" ");
    return (
        <span ref={ref} className="inline">
            {words.map((w, i) => {
                const start = i / words.length;
                const end = start + 1 / words.length;
                return (
                    <Word
                        key={i}
                        progress={scrollYProgress}
                        start={start}
                        end={end}
                    >
                        {w}
                    </Word>
                );
            })}
        </span>
    );
};

const Vision = () => {
    return (
        <section
            id="vision"
            data-testid={TID.visionSection}
            className="relative py-32 md:py-48 px-6"
        >
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(900px circle at 50% 30%, rgba(139,92,246,0.07), transparent 60%)",
                }}
            />

            <div className="max-w-5xl mx-auto relative z-10">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="font-mono text-xs tracking-[0.4em] uppercase text-violet-500 dark:text-brand-blue/80"
                >
                    05 — The Vision
                </motion.p>

                <h2 className="mt-8 font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[1.05] text-[#111827] dark:text-white max-w-5xl">
                    <ScrollText text="We believe education deserves better infrastructure." />
                </h2>

                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                    {points.map((p, i) => (
                        <motion.div
                            key={p}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.7,
                                delay: i * 0.08,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="border-t border-black/[0.07] dark:border-white/10 pt-6"
                        >
                            <span className="font-mono text-[11px] tracking-[0.3em] text-violet-500 dark:text-brand-purple/80">
                                / 0{i + 1}
                            </span>
                            <p className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-gradient-brand">
                                {p}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Vision;
